// The Web Audio half of the score: a lookahead scheduler, a small synth per
// instrument, and four layer gains that decide how much of the arrangement is
// audible. All of the composition -- tuning, form, patterns, the shape of the
// arc -- lives in musicParams.ts, where it is pure and tested.
//
// Two rules run through the whole file:
//
//   Nothing here goes through audio.ts's shot(). That path is voice-capped at
//   sixteen and steals the oldest voice already sounding, which a sustained
//   music note always is; routed through it, the score would cut itself off.
//
//   The music never competes with the game. audio.ts owns the bus this
//   attaches to, and it ducks that bus on every one-shot.
import {
  BANDS,
  MUSIC_ROOT_HZ,
  CHORDS,
  RESOLVE_HZ,
  STEPS_PER_BAR,
  STEPS_PER_BEAT,
  STEPS_PER_PHRASE,
  busToneHz,
  chordForStep,
  chordToneHz,
  foldInto,
  PERCUSSION,
  intensity01,
  isFillStep,
  layerGains,
  recovery01,
  speedTrim,
  stepDurationS,
  themeForSeed,
  type Chord,
  type LayerGains,
} from "./musicParams";
import { musicBus, setMusicPresent } from "./audio";
import { MUSIC_KEY } from "./constants";
import { mulberry32, type Rng } from "./rng";

type LayerName = keyof LayerGains;

const LAYER_NAMES = ["pulse", "groove", "momentum", "charge"] as const;

type MusicKit = {
  ctx: AudioContext;
  noise: AudioBuffer;
  out: GainNode;
  tone: BiquadFilterNode;
  layers: Record<LayerName, GainNode>;
};

export type MusicState = {
  phase: "title" | "playing" | "results";
  progress: number;
  speed01: number;
  crashes: number;
};

// How far ahead of the clock the scheduler commits notes. Long enough to ride
// out a dropped frame, a 55ms hitstop or a garbage collection without a gap,
// short enough that a crash still changes the next bar and not the one after.
const LOOKAHEAD_S = 0.28;

// Past this the scheduler assumes the page was parked rather than merely slow,
// and restarts the grid at now instead of firing every missed step at once.
const RESYNC_S = 0.5;

let kit: MusicKit | null = null;
let enabled = loadPref();
let theme = themeForSeed(0);
let rng: Rng = mulberry32(0);
let mode: "idle" | "riding" | "results" = "idle";
let step = 0;
let nextStepTime = 0;
let crashAt = -Infinity;
let lastCrashCount = 0;
let gains: LayerGains = { pulse: 0, groove: 0, momentum: 0, charge: 0 };

function loadPref(): boolean {
  try {
    return typeof localStorage === "undefined" || localStorage.getItem(MUSIC_KEY) !== "0";
  } catch {
    return true;
  }
}

function savePref(next: boolean): void {
  try {
    localStorage.setItem(MUSIC_KEY, next ? "1" : "0");
  } catch {}
}

// ---------------------------------------------------------------------------
// Graph.

function ensureKit(): MusicKit | null {
  if (kit) return kit;
  const bus = musicBus();
  if (!bus) return null;
  const { ctx, noise, dest } = bus;

  const out = ctx.createGain();
  out.gain.value = 0;
  out.connect(dest);

  // Glue: the score compresses itself before it reaches the master limiter, so
  // a dense bar never spends the headroom a crash is about to need. Without it
  // the limiter would ride the music and drag the sound effects down with it.
  // The threshold is set against the summed peaks of a full bar, not far below
  // them -- low enough and this stops being glue and starts being a fader.
  const glue = ctx.createDynamicsCompressor();
  glue.threshold.value = -12;
  glue.knee.value = 8;
  glue.ratio.value = 3;
  glue.attack.value = 0.006;
  glue.release.value = 0.14;
  glue.connect(out);

  const tone = ctx.createBiquadFilter();
  tone.type = "lowpass";
  tone.frequency.value = 5000;
  tone.Q.value = 0.6;
  tone.connect(glue);

  const layers = {} as Record<LayerName, GainNode>;
  for (const name of LAYER_NAMES) {
    const g = ctx.createGain();
    g.gain.value = 0;
    g.connect(tone);
    layers[name] = g;
  }

  kit = { ctx, noise, out, tone, layers };
  return kit;
}

function endWith(src: AudioScheduledSourceNode, nodes: AudioNode[]): void {
  src.onended = () => {
    for (const n of nodes) {
      try {
        n.disconnect();
      } catch {}
    }
  };
}

// Percussive envelope. Exponential both ways: a linear attack on a synth drum
// reads as a click, and a linear tail reads as a cut.
function env(ctx: AudioContext, t: number, peak: number, attack: number, decay: number): GainNode {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + decay);
  return g;
}

type ToneOpts = {
  wave: OscillatorType;
  hz: number;
  glideTo?: number;
  glideS?: number;
  detune?: number;
  peak: number;
  attack: number;
  decay: number;
  lowpass?: number;
  lowpassTo?: number;
};

function toneVoice(t: number, dest: AudioNode, o: ToneOpts): void {
  const k = kit;
  if (!k) return;
  const { ctx } = k;
  const osc = ctx.createOscillator();
  osc.type = o.wave;
  osc.frequency.setValueAtTime(o.hz, t);
  if (o.glideTo) osc.frequency.exponentialRampToValueAtTime(o.glideTo, t + (o.glideS ?? o.decay));
  if (o.detune) osc.detune.value = o.detune;

  const g = env(ctx, t, o.peak, o.attack, o.decay);
  const nodes: AudioNode[] = [osc, g];
  let head: AudioNode = osc;
  if (o.lowpass) {
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(o.lowpass, t);
    if (o.lowpassTo) lp.frequency.exponentialRampToValueAtTime(o.lowpassTo, t + o.decay * 0.7);
    lp.Q.value = 1.1;
    osc.connect(lp);
    head = lp;
    nodes.push(lp);
  }
  head.connect(g);
  g.connect(dest);
  osc.start(t);
  osc.stop(t + o.decay + 0.05);
  endWith(osc, nodes);
}

type NoiseOpts = {
  type: BiquadFilterType;
  hz: number;
  q?: number;
  peak: number;
  attack: number;
  decay: number;
};

function noiseVoice(t: number, dest: AudioNode, o: NoiseOpts): void {
  const k = kit;
  if (!k) return;
  const { ctx, noise } = k;
  const src = ctx.createBufferSource();
  src.buffer = noise;
  src.loop = true;
  const f = ctx.createBiquadFilter();
  f.type = o.type;
  f.frequency.value = o.hz;
  if (o.q !== undefined) f.Q.value = o.q;
  const g = env(ctx, t, o.peak, o.attack, o.decay);
  src.connect(f);
  f.connect(g);
  g.connect(dest);
  src.start(t, rng() * (noise.duration - 0.6));
  src.stop(t + o.decay + 0.05);
  endWith(src, [src, f, g]);
}

// ---------------------------------------------------------------------------
// Instruments. Every register here is asserted against BANDS in the unit tests:
// no fundamental lands in 500-1400Hz, where the pickup ladder, the finish
// arpeggio, the airborne whistle and the tree crack all live.

function kick(t: number, dest: AudioNode): void {
  toneVoice(t, dest, {
    wave: "sine",
    hz: PERCUSSION.kick.from,
    glideTo: PERCUSSION.kick.to,
    glideS: 0.085,
    peak: 0.45,
    attack: 0.004,
    decay: 0.26,
  });
  noiseVoice(t, dest, {
    type: "bandpass",
    hz: PERCUSSION.kick.click,
    q: 1.2,
    peak: 0.03,
    attack: 0.001,
    decay: 0.02,
  });
}

function sub(t: number, chord: Chord, dest: AudioNode, dur: number): void {
  toneVoice(t, dest, {
    wave: "sine",
    hz: foldInto(MUSIC_ROOT_HZ * chord.root, BANDS.sub),
    peak: 0.18,
    attack: 0.012,
    decay: Math.min(0.5, dur * 1.8),
  });
}

function snare(t: number, dest: AudioNode): void {
  toneVoice(t, dest, {
    wave: "triangle",
    hz: PERCUSSION.snare.from,
    glideTo: PERCUSSION.snare.to,
    peak: 0.12,
    attack: 0.002,
    decay: 0.1,
  });
  noiseVoice(t, dest, {
    type: "bandpass",
    hz: PERCUSSION.snare.noise,
    q: 0.9,
    peak: 0.13,
    attack: 0.002,
    decay: 0.14,
  });
}

function hat(t: number, dest: AudioNode, open: boolean): void {
  noiseVoice(t, dest, {
    type: "highpass",
    hz: PERCUSSION.hat,
    q: 0.7,
    peak: open ? 0.04 : 0.045,
    attack: 0.001,
    decay: open ? 0.19 : 0.036,
  });
}

function shaker(t: number, dest: AudioNode): void {
  noiseVoice(t, dest, {
    type: "bandpass",
    hz: PERCUSSION.shaker,
    q: 0.8,
    peak: 0.03,
    attack: 0.004,
    decay: 0.05,
  });
}

function bass(t: number, chord: Chord, dest: AudioNode, dur: number, degree: number): void {
  toneVoice(t, dest, {
    wave: theme.bassWave,
    hz: chordToneHz(chord, degree, BANDS.bass),
    peak: 0.16,
    attack: 0.006,
    decay: Math.min(0.42, dur * 1.7),
    // The lowpass is the register budget made real: a sawtooth at 130Hz has
    // partials straight through the pickup band, and this is what stops them.
    lowpass: 900,
    lowpassTo: 380,
  });
}

function pluck(t: number, chord: Chord, dest: AudioNode, degree: number): void {
  const hz = chordToneHz(chord, degree, BANDS.pluck);
  for (const detune of [-6, 6]) {
    toneVoice(t, dest, {
      wave: theme.pluckWave,
      hz,
      detune,
      peak: 0.05,
      attack: 0.003,
      decay: 0.22,
      lowpass: 1900,
      lowpassTo: 620,
    });
  }
}

function stab(t: number, chord: Chord, dest: AudioNode, decay: number): void {
  for (let i = 0; i < 3; i++) {
    toneVoice(t, dest, {
      wave: "triangle",
      hz: chordToneHz(chord, i, BANDS.stab),
      detune: (rng() * 2 - 1) * 5,
      peak: 0.04,
      attack: 0.01,
      decay,
      lowpass: 900,
    });
  }
}

function hook(t: number, chord: Chord, dest: AudioNode, degree: number): void {
  const hz = chordToneHz(chord, degree, BANDS.hook);
  toneVoice(t, dest, { wave: "sine", hz, peak: 0.07, attack: 0.004, decay: 0.85 });
  toneVoice(t, dest, {
    wave: "sine",
    hz: hz * 2,
    detune: 5,
    peak: 0.018,
    attack: 0.004,
    decay: 0.55,
  });
}

// ---------------------------------------------------------------------------
// Scheduling.

function scheduleRide(atStep: number, t: number, dur: number): void {
  const k = kit;
  if (!k) return;
  const s = atStep % STEPS_PER_BAR;
  const phraseStep = atStep % STEPS_PER_PHRASE;
  const chord = chordForStep(theme, atStep);
  const L = k.layers;

  if (theme.kick.includes(s)) {
    kick(t, L.pulse);
    sub(t, chord, L.pulse, dur);
  }
  if (theme.hat.includes(s)) hat(t, L.pulse, false);

  // A layer that is not audible is not scheduled. The arrangement is still
  // continuous -- the gains decide what is heard -- but there is no reason to
  // build and tear down oscillators nobody can hear for the first half of a run.
  if (gains.groove > 0.02) {
    if (theme.snare.includes(s) || isFillStep(atStep)) snare(t, L.groove);
    const bi = theme.bass.indexOf(s);
    if (bi >= 0) bass(t, chord, L.groove, dur, bi % 3);
    const pi = theme.pluck.indexOf(s);
    if (pi >= 0) pluck(t, chord, L.groove, (pi + 1) % 3);
  }

  if (gains.momentum > 0.02) {
    if (s === 0 || s === 10) stab(t, chord, L.momentum, s === 0 ? 0.6 : 0.32);
    if (s === 2 || s === 14) hat(t, L.momentum, true);
    if (s % 2 === 1) shaker(t, L.momentum);
  }

  if (gains.charge > 0.02) {
    const hi = theme.hook.indexOf(phraseStep);
    if (hi >= 0) hook(t, chord, L.charge, theme.hookDegrees[hi]!);
    if (s === 8) snare(t, L.charge);
  }
}

// The results bed: the same harmony, none of the drive. One slow pad every two
// bars, so the card has a floor under it instead of dropping back to bare wind.
// Routed past the layer gains, like the resolve -- see musicFinish.
function scheduleResults(atStep: number, t: number): void {
  const k = kit;
  if (!k) return;
  if (atStep % (STEPS_PER_BAR * 2) !== 0) return;
  const chord = chordForStep(theme, atStep);
  for (let i = 0; i < 3; i++) {
    toneVoice(t, k.tone, {
      wave: "triangle",
      hz: chordToneHz(chord, i, BANDS.stab),
      detune: (rng() * 2 - 1) * 4,
      peak: 0.028,
      attack: 1.2,
      decay: 4.6,
      lowpass: 1100,
    });
  }
}

function runScheduler(now: number): void {
  const dur = stepDurationS(theme.bpm);
  if (nextStepTime < now - RESYNC_S) nextStepTime = now;
  while (nextStepTime < now + LOOKAHEAD_S) {
    if (mode === "riding") scheduleRide(step, nextStepTime, dur);
    else scheduleResults(step, nextStepTime);
    nextStepTime += dur;
    step++;
  }
}

// ---------------------------------------------------------------------------
// Lifecycle.

export function musicStart(seed: number): void {
  // The transport resets whether or not there is a score to hear, so switching
  // the music on mid-run joins this run rather than resuming the last one.
  theme = themeForSeed(seed);
  rng = mulberry32((seed >>> 0) ^ 0x5f3a1c9d);
  mode = "riding";
  step = 0;
  crashAt = -Infinity;
  lastCrashCount = 0;

  const k = ensureKit();
  if (!k) return;
  const t = k.ctx.currentTime + 0.06;
  nextStepTime = t;
  if (!enabled) return;

  // The launch: a short riser into the downbeat, so the rhythm is already
  // running by the time the tube is moving rather than fading up behind it.
  const riser = k.ctx.createOscillator();
  riser.type = "sawtooth";
  riser.frequency.setValueAtTime(PERCUSSION.riser.from, t);
  riser.frequency.exponentialRampToValueAtTime(PERCUSSION.riser.to, t + 0.22);
  const rg = env(k.ctx, t, 0.07, 0.12, 0.26);
  const rlp = k.ctx.createBiquadFilter();
  rlp.type = "lowpass";
  rlp.frequency.setValueAtTime(500, t);
  rlp.frequency.exponentialRampToValueAtTime(1400, t + 0.24);
  riser.connect(rlp);
  rlp.connect(rg);
  rg.connect(k.tone);
  riser.start(t);
  riser.stop(t + 0.32);
  endWith(riser, [riser, rlp, rg]);
}

// The finish resolves rather than stops: the arrangement runs to the next
// eighth note, lands on the open fifth in RESOLVE_HZ and rings under sfxFinish.
//
// The resolve is routed straight to the bus, past the layer gains. Those are
// scaled by crash recovery, and a crash in sight of the finish line would
// otherwise mute the payoff at exactly the moment it is most deserved.
export function musicFinish(isBest: boolean): void {
  if (mode !== "riding") return;
  mode = "results";
  const k = kit;
  if (!k || !enabled) return;

  const dur = stepDurationS(theme.bpm);
  const eighth = STEPS_PER_BEAT / 2;
  const at = nextStepTime + ((eighth - (step % eighth)) % eighth) * dur;

  for (const hz of RESOLVE_HZ) {
    toneVoice(at, k.tone, {
      wave: "triangle",
      hz,
      detune: (rng() * 2 - 1) * 4,
      peak: 0.1,
      attack: 0.012,
      decay: 2.6,
      lowpass: 2400,
    });
  }
  toneVoice(at, k.tone, {
    wave: "sine",
    hz: MUSIC_ROOT_HZ,
    peak: 0.16,
    attack: 0.01,
    decay: 1.4,
  });
  noiseVoice(at, k.tone, {
    type: "highpass",
    hz: PERCUSSION.cymbal,
    q: 0.7,
    peak: isBest ? 0.09 : 0.06,
    attack: 0.01,
    decay: isBest ? 2.2 : 1.4,
  });
  // A best keeps climbing, the way the fanfare does: one bell over the resolve,
  // on the beat after it.
  if (isBest) hook(at + dur * eighth, CHORDS.C, k.tone, 2);
}

export function tickMusic(state: MusicState): void {
  const k = ensureKit();
  if (!k) return;
  const now = k.ctx.currentTime;

  if (!enabled) {
    k.out.gain.setTargetAtTime(0, now, 0.12);
    return;
  }

  if (mode === "riding" && state.phase === "playing") {
    if (state.crashes > lastCrashCount) {
      lastCrashCount = state.crashes;
      crashAt = now;
    }
    gains = layerGains(state.progress, recovery01(now - crashAt));
  } else {
    // Idle and results both play nothing through the layers; the results bed
    // and the resolve go straight to the bus.
    gains = { pulse: 0, groove: 0, momentum: 0, charge: 0 };
  }

  for (const name of LAYER_NAMES) {
    k.layers[name].gain.setTargetAtTime(gains[name], now, 0.12);
  }
  // The resolve and the results bed have earned full brightness; the ride's
  // tone follows how much of the arrangement is actually playing.
  const toneHz = mode === "results" ? 16000 : busToneHz(intensity01(gains));
  k.tone.frequency.setTargetAtTime(toneHz, now, 0.25);
  k.out.gain.setTargetAtTime(speedTrim(state.speed01), now, 0.15);

  if (mode !== "idle") runScheduler(now);
}

export function isMusicOn(): boolean {
  return enabled;
}

export function setMusicOn(next: boolean): void {
  enabled = next;
  savePref(next);
  setMusicPresent(next);
  const k = kit;
  if (!k) return;
  if (next) {
    // Rejoin the grid rather than replaying the bar that went by while it was off.
    nextStepTime = k.ctx.currentTime;
  } else {
    k.out.gain.setTargetAtTime(0, k.ctx.currentTime, 0.12);
  }
}

/** For the QA harness: which arrangement this run drew, and what it is doing. */
export function musicDebugState(): { theme: string; mode: string; on: boolean } {
  return { theme: theme.id, mode, on: enabled };
}

// Told to audio.ts before the first unlock, so the ambience bed is built at the
// right level rather than trimmed a moment after the graph starts.
setMusicPresent(enabled);
